import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, FolderKanban, Eye, MessageSquareDot, DollarSign, Wrench, Globe, TrendingUp, BarChart3, CreditCard, RotateCcw, Zap, ChevronRight, BookOpen, Send } from "lucide-react";
import { useFetch, Card, Loader, PageHeader, Reveal, Badge } from "../ui";
import { api, apiError } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const CHIP_STYLES = {
  "ON TRACK": "bg-[#C6FF00]/10 text-[#C6FF00]", "WAITING ON CLIENT": "bg-amber-500/15 text-amber-400",
  "WAITING ON HUGHLAUNCH": "bg-sky-500/15 text-sky-400", "FOUNDER APPROVAL": "bg-purple-500/15 text-purple-400",
  "AT RISK": "bg-red-500/15 text-red-400", "COMPLETE": "bg-emerald-500/15 text-emerald-400",
};
export const Chip = ({ label }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${CHIP_STYLES[label] || "bg-[#14161b] text-[#8A939B]"}`}>{label}</span>;

const FounderActions = ({ tick }) => {
  const { data } = useFetch("/admin/delivery/command-center", [tick]);
  const navigate = useNavigate();
  if (!data) return null;
  const c = data.counters;
  const quick = [["Audits To Review", c.audits_to_review], ["Proposals Awaiting", c.proposals_awaiting], ["Client Blocked", c.client_blocked], ["At Risk", c.at_risk], ["Founder Approvals", c.founder_approvals], ["Onboarding", c.clients_onboarding]];
  return (
    <Reveal>
      <Card className="mt-6 p-6" data-testid="founder-actions">
        <div className="flex items-center gap-2.5"><Zap className="h-5 w-5 text-[#C6FF00]" /><h3 className="text-lg font-semibold">Founder Actions</h3></div>
        <p className="mt-3 rounded-xl bg-[#14161b] p-3 text-sm text-[#c8cdd3]" data-testid="founder-digest">{data.digest}</p>
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
          {quick.map(([k, v]) => (
            <div key={k} className={`rounded-xl p-2.5 text-center ${v > 0 ? "bg-[#C6FF00]/10" : "bg-[#14161b]"}`}>
              <p className={`text-xl font-extrabold ${v > 0 ? "text-[#C6FF00]" : "text-white"}`}>{v}</p>
              <p className="text-[9px] font-bold uppercase text-[#8A939B]">{k}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5">
          {data.founder_actions.length === 0 && <p className="text-sm text-[#6B7280]" data-testid="no-founder-actions">Nothing genuinely needs you right now.</p>}
          {data.founder_actions.map((a, i) => (
            <button key={i} onClick={() => navigate(a.link)} data-testid={`founder-action-${i}`}
              className="flex w-full items-center justify-between rounded-xl border border-[#1c1f25] bg-[#101216] px-4 py-3 text-left text-sm font-semibold text-white transition-colors hover:border-[#C6FF00]">
              {a.label} <ChevronRight className="h-4 w-4 shrink-0 text-[#C6FF00]" />
            </button>
          ))}
        </div>
      </Card>
    </Reveal>
  );
};

const HealthCard = ({ tick }) => {
  const { data } = useFetch("/admin/delivery/health", [tick]);
  const navigate = useNavigate();
  if (!data || data.health.length === 0) return null;
  return (
    <Reveal>
      <Card className="mt-6 p-6" data-testid="client-health">
        <h3 className="text-lg font-semibold">Client Health <span className="text-xs font-normal text-[#6B7280]">(internal only)</span></h3>
        <div className="mt-3 space-y-2">
          {data.health.map((h) => (
            <button key={h.client_id} onClick={() => navigate(`/admin/record/${h.client_id}`)} data-testid={`health-${h.client_id}`}
              className="flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border border-[#1c1f25] bg-[#101216] px-4 py-3 text-left hover:border-[#3a3f47]">
              <span className="font-bold text-white">{h.company}</span>
              <span className="flex items-center gap-3">
                {h.reasons.length > 0 && <span className="text-xs text-[#8A939B]">{h.reasons.slice(0, 2).join(" · ")}</span>}
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-extrabold ${h.label === "HEALTHY" ? "bg-[#C6FF00]/10 text-[#C6FF00]" : h.label === "WATCH" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>{h.label} · {h.score}</span>
              </span>
            </button>
          ))}
        </div>
      </Card>
    </Reveal>
  );
};

const NURTURE_STATUS_STYLE = {
  SENT: "bg-[#C6FF00]/10 text-[#C6FF00]", SCHEDULED: "bg-[#14161b] text-[#8A939B]",
  UPCOMING: "bg-amber-500/15 text-amber-400", "NOT DEPLOYED": "bg-[#14161b] text-[#6B7280]",
};

export const NurtureDetailModal = ({ leadId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [sending, setSending] = useState(0);
  const load = (id) => api.get(`/admin/acquisition/ebook-nurture/${id}`).then(({ data }) => setDetail(data)).catch(() => onClose());
  useEffect(() => {
    setDetail(null);
    if (leadId) load(leadId);
  }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!leadId) return null;

  const sendNow = async (n) => {
    setSending(n);
    try {
      await api.post(`/admin/acquisition/ebook-nurture/${leadId}/send/${n}`);
      toast.success(`Email ${n} sent to ${detail.lead.email}`);
      load(leadId);
    } catch (e) { toast.error(apiError(e)); }
    setSending(0);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="nurture-detail-modal">
      <div className="w-full max-w-lg rounded-2xl border border-[#23262c] bg-[#0E1013] p-6" onClick={(e) => e.stopPropagation()}>
        {!detail ? <p className="text-sm text-[#6B7280]">Loading…</p> : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-black text-white" data-testid="nurture-detail-name">{detail.lead.name || detail.lead.email}</p>
                <p className="text-xs text-[#8A939B]">{detail.lead.email} · downloaded {detail.lead.downloaded_at ? new Date(detail.lead.downloaded_at).toLocaleDateString() : "—"}</p>
              </div>
              <button onClick={onClose} data-testid="nurture-detail-close" className="text-[#6B7280] hover:text-white text-xl leading-none">✕</button>
            </div>
            <p className="mt-3 text-xs text-[#8A939B]">
              {detail.deployed_at ? <>Deployed {new Date(detail.deployed_at).toLocaleDateString()}{detail.auto ? " (auto-enrolled)" : ""} · <span className="font-bold text-white">{detail.sent}/4 sent</span></> : "Sequence not deployed yet."}
            </p>
            {detail.outcome && (
              <p className={`mt-2 rounded-xl px-3 py-2 text-xs font-bold ${detail.outcome.startsWith("Moved forward") ? "bg-[#C6FF00]/10 text-[#C6FF00]" : "bg-[#14161b] text-[#8A939B]"}`} data-testid="nurture-detail-outcome">{detail.outcome}</p>
            )}
            <div className="mt-4 space-y-2">
              {detail.emails.map((e) => (
                <div key={e.n} className="flex items-center justify-between gap-3 rounded-xl bg-[#101216] px-3.5 py-2.5" data-testid={`nurture-email-${e.n}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">{e.n}. {e.subject}</p>
                    <p className="text-[11px] text-[#6B7280]">
                      {e.status === "SENT" && e.sent_at ? `Sent ${new Date(e.sent_at).toLocaleString()}` : e.status === "SCHEDULED" ? (e.due_in_days === 0 ? "Sending on next cycle" : `Scheduled in ${e.due_in_days}d`) : e.status === "UPCOMING" ? "Not sent yet — deploy manually anytime" : "Awaiting deploy"}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-2">
                    {e.status !== "SENT" && (
                      <button onClick={() => sendNow(e.n)} disabled={sending > 0} data-testid={`nurture-send-now-${e.n}`}
                        className="rounded-full border border-[#C6FF00]/50 px-3 py-1 text-[9px] font-extrabold uppercase tracking-wider text-[#C6FF00] transition-colors hover:bg-[#C6FF00] hover:text-[#0A0C0F] disabled:opacity-50">
                        {sending === e.n ? "Sending…" : "Send Now"}
                      </button>
                    )}
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-extrabold ${NURTURE_STATUS_STYLE[e.status] || "bg-[#14161b] text-[#8A939B]"}`}>{e.status}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EbookNurture = ({ tick }) => {
  const { data, reload } = useFetch("/admin/acquisition/ebook-nurture", [tick]);
  const [busy, setBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const navigate = useNavigate();
  if (!data) return null;
  const { eligible, active, completed, performance = [] } = data;
  if (eligible.length === 0 && active.length === 0 && completed.length === 0) return null;
  const perfVisible = performance.filter((p) => p.sent > 0 || p.converted_after > 0);

  const deploy = async (leadId) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/acquisition/ebook-nurture/deploy", leadId ? { lead_id: leadId } : { all: true });
      toast.success(r.deployed > 0 ? `Nurture deployed — first email sent to ${r.deployed} lead(s)` : "No eligible leads to deploy");
      reload();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Reveal>
      <Card className="mt-6 p-6" data-testid="ebook-nurture-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <BookOpen className="h-5 w-5 text-[#C6FF00]" />
            <h3 className="text-lg font-semibold">Ebook Nurture</h3>
            <button onClick={() => navigate("/admin/ebook-leads")} data-testid="ebook-nurture-view-leads" className="text-xs font-bold text-[#C6FF00] hover:underline">Playbook Leads →</button>
          </div>
          {eligible.length > 0 && (
            <button onClick={() => deploy(null)} disabled={busy} data-testid="ebook-nurture-deploy-all"
              className="flex items-center gap-1.5 rounded-full bg-[#C6FF00] px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-[#0A0C0F] hover:bg-white disabled:opacity-50">
              <Send className="h-3.5 w-3.5" /> Deploy All ({eligible.length})
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-[#8A939B]">Playbook downloads that haven't moved forward. 4 emails over 12 days — auto-stops the moment they book a call, start a Brand Score, or become a client. New downloads auto-enroll after 2 days of no action.</p>
        {eligible.length > 0 && (
          <div className="mt-4 space-y-1.5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-400">Ready to deploy · {eligible.length}</p>
            {eligible.map((l) => (
              <div key={l.id} onClick={() => setDetailId(l.id)} role="button" tabIndex={0}
                className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-xl border border-[#1c1f25] bg-[#101216] px-4 py-2.5 hover:border-[#3a3f47]" data-testid={`ebook-nurture-eligible-${l.id}`}>
                <span className="min-w-0">
                  <span className="text-sm font-bold text-white">{l.name || l.email}</span>
                  <span className="ml-2 text-xs text-[#6B7280]">{l.email} · downloaded {l.downloaded_at ? new Date(l.downloaded_at).toLocaleDateString() : "—"}</span>
                </span>
                <button onClick={(e) => { e.stopPropagation(); deploy(l.id); }} disabled={busy} data-testid={`ebook-nurture-deploy-${l.id}`}
                  className="rounded-full border border-[#C6FF00]/50 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-[#C6FF00] hover:bg-[#C6FF00] hover:text-[#0A0C0F] transition-colors disabled:opacity-50">
                  Deploy
                </button>
              </div>
            ))}
          </div>
        )}
        {active.length > 0 && (
          <div className="mt-4 space-y-1.5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#C6FF00]">In sequence · {active.length}</p>
            {active.map((l) => (
              <div key={l.id} onClick={() => setDetailId(l.id)} role="button" tabIndex={0}
                className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-xl bg-[#101216] px-4 py-2.5 hover:bg-[#14161b]" data-testid={`ebook-nurture-active-${l.id}`}>
                <span className="text-sm font-semibold text-white">{l.name || l.email} <span className="text-xs font-normal text-[#6B7280]">{l.email}</span></span>
                <span className="flex items-center gap-2 text-xs text-[#8A939B]">
                  {l.auto && <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[9px] font-extrabold text-sky-400">AUTO</span>}
                  <span className="font-bold text-white">{l.sent}/4 sent</span>
                  {l.sent < 4 && <span>· next in {l.next_in_days}d</span>}
                </span>
              </div>
            ))}
          </div>
        )}
        {completed.length > 0 && (
          <div className="mt-4 space-y-1.5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#6B7280]">Finished · {completed.length}</p>
            {completed.map((l) => (
              <div key={l.id} onClick={() => setDetailId(l.id)} role="button" tabIndex={0}
                className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-xl bg-[#101216]/60 px-4 py-2 hover:bg-[#14161b]" data-testid={`ebook-nurture-completed-${l.id}`}>
                <span className="text-sm text-[#8A939B]">{l.name || l.email} <span className="text-xs text-[#4B5563]">{l.email}</span></span>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-extrabold ${l.stopped_reason === "Lead moved forward" ? "bg-[#C6FF00]/10 text-[#C6FF00]" : "bg-[#14161b] text-[#6B7280]"}`}>
                  {l.stopped_reason === "Lead moved forward" ? `CONVERTED AFTER ${l.sent}/4` : `${l.sent}/4 SENT`}
                </span>
              </div>
            ))}
          </div>
        )}
        {perfVisible.length > 0 && (
          <div className="mt-4 border-t border-[#1c1f25] pt-3" data-testid="ebook-nurture-performance">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#8A939B]">Sequence performance</p>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {performance.map((p) => (
                <div key={p.n} className="rounded-xl bg-[#101216] p-2.5 text-center" data-testid={`nurture-perf-${p.n}`}>
                  <p className="text-[9px] font-bold uppercase text-[#6B7280]">Email {p.n}</p>
                  <p className="mt-0.5 text-sm font-extrabold text-white">{p.sent} <span className="text-[10px] font-bold text-[#6B7280]">delivered</span></p>
                  <p className={`text-[10px] font-bold ${p.converted_after > 0 ? "text-[#C6FF00]" : "text-[#4B5563]"}`}>{p.converted_after} moved forward after</p>
                </div>
              ))}
            </div>
          </div>
        )}
        <NurtureDetailModal leadId={detailId} onClose={() => setDetailId(null)} />
      </Card>
    </Reveal>
  );
};

const Pipeline = ({ tick }) => {
  const { data } = useFetch("/admin/delivery/pipeline", [tick]);
  const navigate = useNavigate();
  if (!data) return null;
  const stages = data.stages.filter((s) => s.records.length > 0);
  const ai = stages.findIndex((s) => s.stage === "AUDIT");
  const ri = stages.findIndex((s) => s.stage === "AUDIT REVIEW");
  if (ai > -1 && ri > -1 && ri > ai) stages.splice(ai, 0, stages.splice(ri, 1)[0]);
  return (
    <Reveal>
      <Card className="mt-6 p-6" data-testid="lifecycle-pipeline">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Client Lifecycle Pipeline</h3>
          <button onClick={() => navigate("/admin/automations")} data-testid="go-automations-btn" className="text-xs font-bold text-[#C6FF00] hover:underline">Automations →</button>
        </div>
        {stages.length === 0 ? <p className="mt-4 text-sm text-[#6B7280]">No active records yet.</p> : (
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
            {stages.map((s) => (
              <div key={s.stage} className="w-56 shrink-0 rounded-xl bg-[#101216] p-3" data-testid={`pipeline-col-${s.stage.replace(/\s+/g, "-").toLowerCase()}`}>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#C6FF00]">{s.stage} <span className="text-[#8A939B]">· {s.records.length}</span></p>
                <div className="mt-2 space-y-2">
                  {s.records.map((r) => (
                    <button key={r.id} onClick={() => navigate(r.link)} data-testid={`pipeline-card-${r.id}`}
                      className="w-full rounded-lg border border-[#1c1f25] bg-[#14161b] p-2.5 text-left transition-colors hover:border-[#C6FF00]">
                      <p className="truncate text-sm font-bold text-white">{r.company || r.name}</p>
                      <div className="mt-1.5 flex items-center justify-between gap-1">
                        <Chip label={r.chip} />
                        {typeof r.progress === "number" && <span className="text-[10px] font-bold text-[#8A939B]">{r.progress}%</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </Reveal>
  );
};

export default function CommandCenter() {
  const [tick, setTick] = useState(0);
  const { data, loading, reload } = useFetch("/admin/summary", [tick]);
  const { data: pay, reload: reloadPay } = useFetch("/admin/payments", [tick]);
  const navigate = useNavigate();
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    const onVis = () => { if (document.visibilityState === "visible") setTick((t) => t + 1); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  if (loading && !data) return <Loader />;
  if (!data) return <Loader />;

  const metrics = [
    { label: "Active Clients", value: data.active_clients, icon: Users, to: "/admin/clients" },
    { label: "Active Projects", value: data.active_projects, icon: FolderKanban, to: "/admin/clients" },
    { label: "Awaiting Approval", value: data.awaiting_approval, icon: Eye, to: "/admin/clients" },
    { label: "Open Revisions", value: data.open_revisions, icon: MessageSquareDot, to: "/admin/clients" },
    { label: "Outstanding Payments", value: money(data.outstanding_payments), icon: DollarSign, to: "/admin/clients" },
    { label: "Maintenance Requests", value: data.maintenance_requests, icon: Wrench, to: "/admin/tasks" },
    { label: "Sites Managed", value: data.sites_managed, icon: Globe, to: "/admin/clients" },
    { label: "Monthly Revenue", value: money(data.monthly_revenue), icon: TrendingUp, to: "/admin/clients" },
    { label: "Leads Generated", value: data.leads_generated, icon: BarChart3, to: "/admin/inbox" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader eyebrow="Hugh Launch" title="Command Center"
          subtitle="Clients, projects, growth systems, payments and digital presence — all in one place." />
        <button
          onClick={() => { setTick((t) => t + 1); reload(); reloadPay(); }}
          data-testid="reset-metrics-btn"
          className="flex items-center gap-2 rounded-full border border-[#2A2D34] px-4 py-2 text-sm font-semibold text-[#8A939B] transition-colors hover:border-[#C6FF00] hover:text-[#C6FF00]"
        >
          <RotateCcw className="h-4 w-4" /> Reset
        </button>
      </div>
      <FounderActions tick={tick} />
      <Pipeline tick={tick} />
      <EbookNurture tick={tick} />
      <HealthCard tick={tick} />
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3">
        {metrics.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.04}>
            <button onClick={() => navigate(m.to)} className="block w-full text-left" data-testid={`metric-link-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Card hover className="cursor-pointer p-5 transition-colors hover:border-[#C6FF00]/60" data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <m.icon className="h-5 w-5 text-[#C6FF00]" />
                </div>
                <p className="mt-4 text-3xl font-bold text-white">{m.value}</p>
                <p className="mt-1 text-sm text-[#8A939B]">{m.label}</p>
              </Card>
            </button>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.28}>
        <Card className="mt-6 p-6" data-testid="revenue-feed">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <CreditCard className="h-5 w-5 text-[#C6FF00]" />
              <h3 className="text-lg font-semibold">Revenue Feed</h3>
            </div>
            {pay && (
              <div className="flex items-center gap-5 text-sm">
                <span className="text-[#8A939B]">Card: <span className="font-bold text-white">{money(pay.card_revenue)}</span></span>
                <span className="text-[#8A939B]">Total: <span className="font-bold text-[#C6FF00]">{money(pay.total_revenue)}</span></span>
              </div>
            )}
          </div>
          <div className="mt-5">
            {!pay ? (
              <p className="text-sm text-[#6B7280]">Loading…</p>
            ) : pay.payments.length === 0 ? (
              <p className="text-sm text-[#6B7280]" data-testid="revenue-empty">No payments yet. They'll appear here the moment a client pays.</p>
            ) : (
              <div className="divide-y divide-[#141821]">
                {pay.payments.slice(0, 8).map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-3" data-testid={`revenue-row-${p.id}`}>
                    <div>
                      <p className="font-semibold text-white">{p.company_name}</p>
                      <p className="text-xs text-[#6B7280]">{p.date} · {p.method_ref}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-white">{money(p.amount)}</span>
                      <Badge status={p.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </Reveal>

      <Reveal delay={0.3}>
        <Card className="mt-6 flex flex-col items-start justify-between gap-4 p-6 sm:flex-row sm:items-center">
          <div><h3 className="text-lg font-semibold">Manage your clients</h3><p className="text-sm text-[#8A939B]">Open a client to update stages, respond to revisions, record payments and more.</p></div>
          <button onClick={() => navigate("/admin/clients")} data-testid="go-clients-btn" className="rounded-full bg-[#C6FF00] px-5 py-2.5 text-sm font-semibold text-[#0A0C0F] hover:bg-white">View Clients</button>
        </Card>
      </Reveal>
    </div>
  );
}
